/* eslint-disable */
import _get from "lodash/get";
import _isBoolean from "lodash/isBoolean";
import _cloneDeep from "lodash/cloneDeep";
import _sortBy from "lodash/sortBy";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import {
  parseAutocompleteForBackend,
  parseDateForBackend,
  parseFilterType,
  parseNumberForBackend,
  parseStatusForBackend,
  parseTextForBackend,
} from "./parsers";
import { FilterTypes } from "@/app-search/enums/enums";

// consts
const CONFIG_WHITELIST = ["show"];

/**
 * Parse config position
 *
 * @param {object} defaultConfig: default config data
 * @param {object} storeConfig: store config data
 * @returns {object}
 */
export function parseConfigPosition(defaultConfig: any, storeConfig: any): any {
  const config =
    storeConfig === null
      ? defaultConfig
      : parseConfig(defaultConfig, storeConfig);
  return Object.fromEntries(
    Object.entries(config).sort(
      ([, a]: any, [, b]: any) => a.position - b.position
    )
  );
}

/**
 * Parse default config data and data from DB (internalUsage)
 *
 * @param {object} defaultConfig: default config data
 * @param {object} storeConfig: store config data
 * @returns {object}
 */
function parseConfig(defaultConfig: any, storeConfig: any): any {
  const parsedData = Object.entries(defaultConfig).map(([key]: any) => {
    // get config from DB if local config is not "readOnly", have "show" prop as a boolean value and "key" exists in config from DB
    if (
      !defaultConfig[key].readOnly &&
      _isBoolean(defaultConfig[key].show) &&
      storeConfig[key]
    ) {
      // get only "CONFIG_WHITELIST" props from DB config
      const whitelistProps = CONFIG_WHITELIST.map(
        mapParseWhitelist.bind(null, defaultConfig[key], storeConfig[key])
      );
      const parsedProps = Object.fromEntries(whitelistProps);

      return [key, { ...defaultConfig[key], ...parsedProps }];
    }

    return [key, defaultConfig[key]];
  });

  return Object.fromEntries(parsedData);
}

/**
 * Callback for map parse whitelist
 *
 * @private
 * @param {object} defaultConfig: default config data
 * @param {object} storeConfig: store config data
 * @param {string} key: "CONFIG_WHITELIST" item
 * @returns {[]}
 */
function mapParseWhitelist(
  defaultConfig: any,
  storeConfig: any,
  key: string
): any {
  const hasKey = Object.keys(storeConfig).includes(key);
  const value = hasKey ? storeConfig[key] : defaultConfig[key];

  return [key, value];
}

/**
 * Parse filter with config to create "result_fields" and add it to filter
 * Adding to "result_fields" only keys with "show: true" or "internalUsage: true"
 *
 * @param {object} filter: filter data
 * @param {object} config: config data
 * @returns {object}
 */
export function parseFieldsResult(filter: any, config: any): any {
  const resultFields = Object.entries(config)
    .filter(([, item]: any) => {
      if (
        (item.hasOwnProperty("internalUsage") && item.internalUsage) ||
        (item.hasOwnProperty("show") && item.show)
      )
        return item;
    })
    .map(([key]: any) => {
      return [key, { raw: {} }];
    });
  // limit search query to below fields only
  const search_fields = {
    booking_products_name: {},
    booking_tickets_name: {},
    // bookings_ticket_quantity: {},
    custom_fields: {},
    customer_email: {},
    customer_first_name: {},
    customer_full_name: {},
    customer_last_name: {},
    reference: {},
    seller_full_name: {},
    seller_organisation_name: {},
  };
  return {
    ...filter,
    options: {
      ...filter.options,
      ...(resultFields.length && {
        result_fields: Object.fromEntries(resultFields),
      }),
      ...{ search_fields: search_fields },
    },
  };
}

/**
 * Parse views configs and create one of all with sort by position
 *
 * @param {object} configs: data with default, public and private configs
 * @returns {object}
 */
export function parseViewsConfigs(configs: any): any {
  const { defaultConfig, publicConfig, privateConfig } = configs;
  let pinnedViewId = "all";
  let viewData: any = defaultConfig.map((item: any) => ({
    ...item,
    name: t(item.name),
  }));

  if (publicConfig) {
    viewData = [...viewData, ...publicConfig];
  }

  if (privateConfig) {
    viewData = [...viewData, ...privateConfig.views];
  }

  viewData = _sortBy(viewData, "position");

  // get pinned view id from "privateConfig" or set "all" as default
  if (privateConfig && privateConfig.pinnedViewId) {
    const hasView = viewData.some(
      (item: any) => item.id === privateConfig.pinnedViewId
    );

    pinnedViewId = hasView ? privateConfig.pinnedViewId : "all";
  }

  return { pinnedViewId, views: viewData };
}

/**
 * Get filters list from current (active) view
 *
 * @param {object} config: data from "parseViewsConfigs"
 * @param {string} id: id of current view
 * @returns {array}
 */
export function parseCurrentViewFilters(config: any, id?: string): any {
  const view = config.views.find((item: any) => item.id === id) || {};
  return view.filters || config.views[0].filters;
}

/**
 * Parse filter for backend
 *
 * @param {object} filter: filter data
 * @returns {object}
 */
export function parseFilter(filter: any): any {
  // TODO - (filters[0]) at this moment we have only one filter array
  // const filters = filter.options.filters[0].reduce((acc: any, item: any) => {
  //   let parsedFilter = null;
  //   const filterType = parseFilterType(item);

  //   if (filterType === FilterTypes.Autocomplete) {
  //     parsedFilter = parseAutocompleteForBackend(item);
  //   }

  //   if (filterType === FilterTypes.Date) {
  //     parsedFilter = parseDateForBackend(item);
  //   }

  //   if (filterType === FilterTypes.Number) {
  //     parsedFilter = parseNumberForBackend(item);
  //   }

  //   if (filterType === FilterTypes.Status) {
  //     parsedFilter = parseStatusForBackend(item);
  //   }

  //   if (filterType === FilterTypes.Text) {
  //     parsedFilter = parseTextForBackend(item);
  //   }

  //   if (parsedFilter) {
  //     if (!acc[parsedFilter.optionType]) {
  //       acc[parsedFilter.optionType] = [];
  //     }

  //     acc[parsedFilter.optionType].push(parsedFilter.value);
  //   }

  //   return acc;
  // }, {});

  // return {
  //   ...filter,
  //   options: {
  //     ...filter.options,
  //     filters,
  //   },
  // };

  const filters = filter.options.filters
    .flat()
    .reduce((acc: any, item: any) => {
      let parsedFilter = null;
      const filterType = parseFilterType(item);

      if (filterType === FilterTypes.Autocomplete) {
        parsedFilter = parseAutocompleteForBackend(item);
      }

      if (filterType === FilterTypes.Date) {
        parsedFilter = parseDateForBackend(item);
      }

      if (filterType === FilterTypes.Number) {
        parsedFilter = parseNumberForBackend(item);
      }

      if (filterType === FilterTypes.Status) {
        parsedFilter = parseStatusForBackend(item);
      }

      if (filterType === FilterTypes.Text) {
        parsedFilter = parseTextForBackend(item);
      }

      if (parsedFilter) {
        if (!acc[parsedFilter.optionType]) {
          acc[parsedFilter.optionType] = [];
        }

        acc[parsedFilter.optionType].push(parsedFilter.value);
      }

      return acc;
    }, {});

  return {
    ...filter,
    options: {
      ...filter.options,
      filters,
    },
  };
}
